import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { 
  Building2, Users, FileText, TrendingUp, DollarSign, 
  BarChart3, ArrowUpRight, ArrowDownRight 
} from 'lucide-react';

const DashboardPage = () => {
  const { api, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setError(null);
      const [statsRes, configsRes] = await Promise.all([
        api.get('/leads/stats').catch(() => ({ data: null })),
        api.get('/configurations/my').catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setConfigs(Array.isArray(configsRes.data) ? configsRes.data : []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const statCards = stats ? [
    { label: 'Total Leads', value: stats.total, icon: FileText, color: 'bg-blue-600' },
    { label: 'Conversion Rate', value: `${stats.conversion_rate}%`, icon: TrendingUp, color: 'bg-green-500' },
    { label: 'Revenue', value: `$${stats.total_revenue?.toLocaleString()}`, icon: DollarSign, color: 'bg-orange-500' },
    { label: 'New Leads', value: stats.new, icon: Users, color: 'bg-purple-500' },
  ] : [];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Dashboard</h1>
          <p className="text-gray-500 dark:text-gray-400">Welcome back, {user?.name}</p>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {statCards.map((stat, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">{stat.label}</p>
                    <p className="text-2xl font-bold text-gray-800 dark:text-white mt-1">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Lead Pipeline */}
        {stats && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Lead Pipeline</h2>
            <div className="grid grid-cols-5 gap-4">
              {[
                { label: 'New', value: stats.new, color: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300' },
                { label: 'Contacted', value: stats.contacted, color: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300' },
                { label: 'Quoted', value: stats.quoted, color: 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300' },
                { label: 'Won', value: stats.closed_won, color: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300' },
                { label: 'Lost', value: stats.closed_lost, color: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300' },
              ].map((stage, index) => (
                <div key={index} className={`${stage.color} rounded-lg p-4 text-center`}>
                  <p className="text-2xl font-bold">{stage.value}</p>
                  <p className="text-sm font-medium">{stage.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Configurations */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Your Recent Configurations</h2>
          {configs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b dark:border-gray-700">
                    <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Name</th>
                    <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Type</th>
                    <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Size</th>
                    <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Price</th>
                    <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {configs.slice(0, 5).map((config) => (
                    <tr key={config.id} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{config.name}</td>
                      <td className="py-3 px-4 capitalize text-gray-600 dark:text-gray-300">{config.building_type}</td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-300">{config.width}&apos; x {config.length}&apos; x {config.height}&apos;</td>
                      <td className="py-3 px-4 font-semibold text-[#e85a2c]">${config.total_price?.toLocaleString()}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          config.status === 'draft' ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300' :
                          config.status === 'quoted' ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-300' :
                          'bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-300'
                        }`}>
                          {config.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">No configurations yet. Start designing!</p>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
